import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import HoldAndWinCoinCollectController from "./HoldAndWinCoinCollectController";
import { safeInit } from "../../../utilities";
import { selectHoldAndWinActive } from "../../../State/selectors";

/**
 * HoldAndWinCoinCollect - Displays particle animations for coin collection
 * Shows particles traveling from coin positions to the collector during collection phase
 */
const HoldAndWinCoinCollect = forwardRef((props, ref) => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const coinCollectControllerRef = useRef(null);
  const coinCollectContainerRef = useRef(null);

  const dimensionsContext = useDimensions();
  const { dimensions, symbolWidth, symbolHeight, device, orientation } =
    dimensionsContext;

  // Initialize controller when container is ready and Hold and Win becomes active
  useEffect(() => {
    if (
      coinCollectContainerRef.current &&
      !coinCollectControllerRef.current &&
      holdAndWinActive
    ) {
      safeInit(() => {
        coinCollectControllerRef.current = new HoldAndWinCoinCollectController(
          coinCollectContainerRef.current,
          dimensions,
          symbolWidth,
          symbolHeight,
          device,
          orientation
        );
      });
    }
  }, [
    holdAndWinActive,
    dimensions,
    symbolWidth,
    symbolHeight,
    device,
    orientation,
  ]);

  // Handle dimension changes
  useEffect(() => {
    if (
      coinCollectControllerRef.current &&
      symbolWidth &&
      symbolHeight &&
      dimensions
    ) {
      coinCollectControllerRef.current.updateDimensions(
        dimensions,
        symbolWidth,
        symbolHeight,
        device,
        orientation
      );
    }
  }, [symbolWidth, symbolHeight, dimensions, device, orientation]);

  // Expose controller methods
  useImperativeHandle(ref, () => ({
    playCollectionAnimation: (reelIndex, symbolIndex, coinValue = 0) => {
      if (!coinCollectControllerRef.current) {
        return Promise.resolve();
      }
      return coinCollectControllerRef.current.playCollectionAnimation(
        reelIndex,
        symbolIndex,
        coinValue
      );
    },
    playEndAnimation: () => {
      if (!coinCollectControllerRef.current) {
        return Promise.resolve();
      }
      return coinCollectControllerRef.current.playEndAnimation();
    },
    playProgressMultiplierAnimation: (multiplierValue) => {
      if (!coinCollectControllerRef.current) {
        return Promise.resolve();
      }
      return coinCollectControllerRef.current.playProgressMultiplierAnimation(
        multiplierValue
      );
    },
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (coinCollectControllerRef.current) {
        coinCollectControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_coin_collect"
      x={0}
      y={0}
      sortableChildren={true}
      zIndex={8}
      ref={coinCollectContainerRef}
      visible={holdAndWinActive}
    />
  );
});

export default HoldAndWinCoinCollect;
